import React, { useEffect, useRef, useState } from 'react';
import { ShieldCheck, Send, Lock } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { ScrollArea } from './ui/scroll-area';
import { apiService, formatApiError } from '../services/api';
import { useToast } from '../hooks/use-toast';

const when = (d) => new Date(d).toLocaleString();

const Bubble = ({ mine, name, body, at, testId }) => (
  <div className={`flex ${mine ? 'justify-end' : 'justify-start'}`} data-testid={testId}>
    <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${mine ? 'bg-rose-500 text-white' : 'bg-gray-100 text-gray-800'}`}>
      <p className={`text-xs mb-0.5 ${mine ? 'text-rose-100' : 'text-gray-500'}`}>{name} · {when(at)}</p>
      <p className="whitespace-pre-wrap">{body}</p>
    </div>
  </div>
);

export const TeamThreadDialog = ({ threadId, open, onOpenChange, onChanged }) => {
  const { toast } = useToast();
  const [t, setT] = useState(null);
  const [error, setError] = useState('');
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [t]);

  const load = () => apiService.inbox.get(threadId).then((r) => { setT(r.data.data); setError(''); onChanged?.(); }).catch((e) => setError(formatApiError(e)));
  useEffect(() => { if (open && threadId) { setT(null); load(); } }, [open, threadId]);

  const send = async () => {
    setBusy(true);
    try { const r = await apiService.inbox.reply(threadId, reply.trim()); setReply(''); toast({ title: 'Reply sent', description: r.data.message }); load(); }
    catch (e) { toast({ title: 'Could not send reply', description: formatApiError(e), variant: 'destructive' }); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="team-thread-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-rose-600" />{t?.subject || 'Message from the FlameLink Team'}</DialogTitle>
          <DialogDescription>Private conversation with the FlameLink team. We will never ask for your password or payment details here.</DialogDescription>
        </DialogHeader>
        {error && <p className="text-sm text-red-600" data-testid="team-thread-error">{error}</p>}
        {t && (
          <>
            <ScrollArea className="max-h-80 pr-2">
              <div className="space-y-2">
                <Bubble name={t.from} body={t.body} at={t.created_at} testId="team-thread-root" />
                {t.replies.map((r) => <Bubble key={r.id} mine={r.author === 'member'} name={r.author === 'member' ? 'You' : r.author_name} body={r.body} at={r.created_at} testId={`team-thread-reply-${r.id}`} />)}
                <div ref={endRef} />
              </div>
            </ScrollArea>
            {t.status === 'closed' ? (
              <p className="text-sm text-gray-500 flex items-center gap-2" data-testid="team-thread-closed"><Lock className="w-4 h-4" />This conversation was closed by our team. Use Help → Contact if you need more help.</p>
            ) : (
              <div className="flex gap-2 items-end">
                <Textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={2} maxLength={3000} placeholder="Write a reply to the FlameLink team" data-testid="team-thread-reply-input" />
                <Button size="sm" onClick={send} disabled={busy || !reply.trim()} data-testid="team-thread-reply-send"><Send className="w-4 h-4" /></Button>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TeamThreadDialog;
